import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@Sentry";
import { useTranslation } from "@i18n";
import { usePortalChat } from "../components/context";
import { DIRECTORY_SEARCH_DEBOUNCE_MS, SEARCH_MAX_LENGTH } from "../portalChat.constants";
import {
  getChatDirectoryRequest,
  createConversationRequest,
  type ChatConversation,
  type ChatDirectoryUser,
} from "../api/portalChat.api";

/** People shown under a search are capped — the picker dialog is the place to browse everyone. */
const PEOPLE_IN_SEARCH_LIMIT = 5;

export type ListGroup = {
  key: string;
  label: string | null;
  rows: ChatConversation[];
  /** Section groups collapse and carry a header menu; type/project headers carry their own. */
  sectionId?: string;
  emptyHint?: boolean;
  /** Set on All-Chats type headers — they get the PRD §4.9/§4.10 category-header ••• menu. */
  typeHeader?: "direct" | "group" | "space";
};

/** Flat row model for the virtualized list: headers, hints and rows in render order. */
export type ChatListRow =
  | { kind: "sectionHeader"; key: string; label: string; sectionId: string; count: number; collapsed: boolean }
  // `typeHeader` is `| undefined` as well as optional: the Hidden-Chats and People headers below
  // carry none, and the All-Chats path copies it straight off a group whose own `typeHeader` is
  // optional, so the row really is built holding `undefined`.
  | { kind: "typeHeader"; key: string; label: string; typeHeader?: "direct" | "group" | "space" | undefined }
  | { kind: "conversation"; key: string; conversation: ChatConversation; hiddenBadge: boolean }
  | { kind: "emptyHint"; key: string }
  | { kind: "person"; key: string; user: ChatDirectoryUser };

/**
 * The conversation-list model shared by the web list pane and the mobile list screen: scope
 * filtering, live sort, search across name/project/preview/section, the sticky unread filter,
 * hidden-chat resurfacing rules, section/project grouping, the flattened virtualization rows,
 * people-in-search, and the open/start-direct actions. The platforms only render rows.
 */
export function useChatListModel() {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const { scope, unreadOnly } = chat;

  const [search, setSearch] = useState("");
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [peopleResults, setPeopleResults] = useState<ChatDirectoryUser[]>([]);
  // PRD §4.8: rows that qualified while the unread filter is ON stay visible after being read,
  // until the filter is toggled off and on again.
  const unreadStickyIds = useRef<Set<string>>(new Set());

  // PRD §4.7: switching scope clears any active search.
  useEffect(() => {
    setSearch("");
  }, [scope]);

  const searchLower = search.trim().toLowerCase();

  // PRD §4.2/§4.11: search also surfaces people you can start a DM with.
  useEffect(() => {
    if (!searchLower || scope === "project") {
      setPeopleResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      getChatDirectoryRequest({ search: search.trim() })
        .then((response) => {
          if (!cancelled && response.data.status) {
            setPeopleResults((response.data.result ?? []).slice(0, PEOPLE_IN_SEARCH_LIMIT));
          }
        })
        .catch((error: unknown) => Sentry.captureException(error));
    }, DIRECTORY_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchLower, search, scope]);

  const scoped = useMemo(() => {
    const rows = chat.conversations.filter((c) => {
      if (scope === "direct" && c.type !== "direct") return false;
      if (scope === "group" && c.type !== "group") return false;
      if (scope === "project" && c.type !== "space") return false;
      return true;
    });
    // Keep list order live: new activity floats a row to the top without a refetch.
    return [...rows].sort((a, b) => (b.lastMessageDate ?? b.createdDate) - (a.lastMessageDate ?? a.createdDate));
  }, [chat.conversations, scope]);

  const matchesSearch = useCallback(
    (c: ChatConversation) => {
      if (!searchLower) return true;
      if (c.name.toLowerCase().includes(searchLower)) return true;
      if (c.projectName?.toLowerCase().includes(searchLower)) return true;
      if (c.lastMessage?.text.toLowerCase().includes(searchLower)) return true;
      const section = chat.sections.find((s) => String(s._id) === String(c.sectionId ?? ""));
      return Boolean(section?.name.toLowerCase().includes(searchLower));
    },
    [searchLower, chat.sections],
  );

  const visible = useMemo(() => {
    // PRD §4.10/§4.11: hidden project chats resurface under Current Project while the user is
    // working in that project (flagged); every other hidden chat is excluded from all views.
    let rows = scoped.filter(
      (c) =>
        !c.hidden ||
        (scope === "project" && chat.currentProjectId && String(c.projectId ?? "") === chat.currentProjectId),
    );
    rows = rows.filter(matchesSearch);
    if (unreadOnly) {
      rows = rows.filter((c) => !c.hidden); // hidden chats never appear under the unread filter
      for (const c of rows) if (c.unreadCount > 0) unreadStickyIds.current.add(c._id);
      rows = rows.filter((c) => c.unreadCount > 0 || unreadStickyIds.current.has(c._id));
    }
    return rows;
  }, [scoped, unreadOnly, matchesSearch, scope, chat.currentProjectId]);

  const hiddenMatches = useMemo(
    () =>
      searchLower
        ? scoped
            .filter(
              (c) =>
                c.hidden &&
                // Already resurfaced into the Current Project group — don't list it twice.
                !(scope === "project" && chat.currentProjectId && String(c.projectId ?? "") === chat.currentProjectId),
            )
            .filter(matchesSearch)
        : [],
    [scoped, searchLower, matchesSearch, scope, chat.currentProjectId],
  );

  /** Toggle wrapper that resets the sticky set (PRD §4.8: off-and-on starts a fresh pass). */
  const toggleUnreadOnly = useCallback(() => {
    unreadStickyIds.current = new Set();
    chat.toggleUnreadOnly();
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- chat.toggleUnreadOnly is a stable core callback
  }, [chat.toggleUnreadOnly]);

  const typeLabel = useCallback(
    (type: ChatConversation["type"]) =>
      type === "direct" ? t("Direct Chats") : type === "group" ? t("Group Chats") : t("Project Chats"),
    [t],
  );

  const groupByProject = useCallback(
    (rows: ChatConversation[]): ListGroup[] => {
      const byProject = new Map<string, ChatConversation[]>();
      for (const c of rows) {
        const key = String(c.projectId ?? "");
        byProject.set(key, [...(byProject.get(key) ?? []), c]);
      }
      const order = [...byProject.keys()].sort((a, b) => {
        if (chat.currentProjectId) {
          if (a === chat.currentProjectId) return -1;
          if (b === chat.currentProjectId) return 1;
        }
        const ia = chat.projectOrder.indexOf(a);
        const ib = chat.projectOrder.indexOf(b);
        if (ia !== -1 || ib !== -1) {
          return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
        }
        const nameA = byProject.get(a)?.[0]?.projectName ?? "";
        const nameB = byProject.get(b)?.[0]?.projectName ?? "";
        return nameA.localeCompare(nameB);
      });
      return order.map((projectId) => ({
        key: `project-${projectId}`,
        label:
          projectId === chat.currentProjectId
            ? `${t("Current Project")} · ${byProject.get(projectId)?.[0]?.projectName ?? ""}`
            : (byProject.get(projectId)?.[0]?.projectName ?? t("Project")),
        rows: byProject.get(projectId) ?? [],
      }));
    },
    [chat.currentProjectId, chat.projectOrder, t],
  );

  const groupBySections = useCallback(
    (rows: ChatConversation[], sectionScope: "direct" | "group"): ListGroup[] => {
      const scopeSections = chat.sections.filter((s) => s.scope === sectionScope).sort((a, b) => a.order - b.order);
      const inSection = new Map<string, ChatConversation[]>();
      const unsectioned: ChatConversation[] = [];
      for (const c of rows) {
        const sectionId = c.sectionId ? String(c.sectionId) : null;
        if (sectionId && scopeSections.some((s) => String(s._id) === sectionId)) {
          inSection.set(sectionId, [...(inSection.get(sectionId) ?? []), c]);
        } else {
          unsectioned.push(c);
        }
      }
      const result: ListGroup[] = [{ key: `${sectionScope}-unsectioned`, label: null, rows: unsectioned }];
      for (const section of scopeSections) {
        const sectionRows = inSection.get(String(section._id)) ?? [];
        // PRD §4.8: sections with no unread chats collapse away entirely under the filter.
        if (unreadOnly && !sectionRows.length) continue;
        if (searchLower && !sectionRows.length && !section.name.toLowerCase().includes(searchLower)) continue;
        result.push({
          key: `section-${String(section._id)}`,
          label: section.name,
          rows: sectionRows,
          sectionId: String(section._id),
          emptyHint: !sectionRows.length,
        });
      }
      return result;
    },
    [chat.sections, unreadOnly, searchLower],
  );

  const groups = useMemo((): ListGroup[] => {
    if (scope === "project") return groupByProject(visible);
    if (scope === "direct" || scope === "group") return groupBySections(visible, scope);
    // All Chats (PRD §4.7/§4.9): type headers, with each type's sections nested beneath it.
    const result: ListGroup[] = [];
    for (const type of ["direct", "group", "space"] as const) {
      const rows = visible.filter((c) => c.type === type);
      if (!rows.length) continue;
      if (type === "space") {
        result.push({ key: "type-space", label: typeLabel(type), rows, typeHeader: "space" });
        continue;
      }
      const nested = groupBySections(rows, type);
      // The unsectioned block carries the type header; the section blocks follow beneath it.
      result.push({ key: `type-${type}`, label: typeLabel(type), rows: nested[0]?.rows ?? [], typeHeader: type });
      result.push(...nested.slice(1));
    }
    return result;
  }, [scope, visible, groupByProject, groupBySections, typeLabel]);

  const listRows = useMemo((): ChatListRow[] => {
    const rows: ChatListRow[] = [];
    for (const group of groups) {
      const collapsed = group.sectionId !== undefined && collapsedSections.has(group.sectionId);
      if (group.label !== null) {
        if (group.sectionId !== undefined) {
          rows.push({
            kind: "sectionHeader",
            key: group.key,
            label: group.label,
            sectionId: group.sectionId,
            count: group.rows.length,
            collapsed,
          });
        } else {
          rows.push({ kind: "typeHeader", key: group.key, label: group.label, typeHeader: group.typeHeader });
        }
      }
      if (!collapsed) {
        for (const conversation of group.rows) {
          rows.push({ kind: "conversation", key: conversation._id, conversation, hiddenBadge: conversation.hidden });
        }
        if (group.emptyHint && !unreadOnly && !searchLower) rows.push({ kind: "emptyHint", key: `${group.key}-hint` });
      }
    }
    if (hiddenMatches.length) {
      rows.push({ kind: "typeHeader", key: "hidden-header", label: t("Hidden Chats") });
      for (const conversation of hiddenMatches) {
        rows.push({ kind: "conversation", key: `hidden-${conversation._id}`, conversation, hiddenBadge: true });
      }
    }
    if (peopleResults.length) {
      rows.push({ kind: "typeHeader", key: "people-header", label: t("People") });
      for (const user of peopleResults) rows.push({ kind: "person", key: `person-${String(user._id)}`, user });
    }
    return rows;
  }, [groups, collapsedSections, unreadOnly, searchLower, hiddenMatches, peopleResults, t]);

  const toggleSectionCollapsed = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  /**
   * Open a list row. PRD §4.11: selecting a hidden chat from search un-hides it, opens it and
   * clears the search. Returns the conversation id so mobile can navigate to the thread screen.
   */
  const openConversationRow = useCallback(
    (conversation: ChatConversation): string => {
      if (conversation.hidden && searchLower) {
        chat.unhideAndOpen(conversation._id);
        setSearch("");
      } else {
        chat.selectConversation(conversation._id);
      }
      return conversation._id;
    },
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- unhideAndOpen/selectConversation are core callbacks
    [searchLower, chat.unhideAndOpen, chat.selectConversation],
  );

  /** Start (or dedupe into) a direct chat from a people-search row; returns the id to open. */
  const startDirectWith = useCallback(
    async (user: ChatDirectoryUser): Promise<string | null> => {
      try {
        const response = await createConversationRequest({ type: "direct", memberIds: [user._id] });
        if (response.data.status && response.data.result) {
          setSearch("");
          await chat.refreshList();
          chat.selectConversation(response.data.result._id);
          return response.data.result._id;
        }
        if (!response.data.status) {
          chat.showServerError(response.data.errorMessage, t("Could not create the chat"));
        }
        return null;
      } catch (error) {
        Sentry.captureException(error);
        chat.showToast(t("Could not create the chat"), false);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks-ext/exhaustive-deps -- core callbacks are referentially managed by the core hook
    [chat.refreshList, chat.selectConversation, chat.showServerError, chat.showToast, t],
  );

  return {
    search,
    setSearch,
    searchLower,
    searchMaxLength: SEARCH_MAX_LENGTH,
    visible,
    hiddenMatches,
    groups,
    listRows,
    peopleResults,
    collapsedSections,
    toggleSectionCollapsed,
    toggleUnreadOnly,
    openConversationRow,
    startDirectWith,
  };
}
